import { hasManualTokens } from "@b3/chain-registry";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { SOLANA_CHAIN_IDS } from "@/lib/chains";
import type { TokenFilterInput, TokenFilterOptions } from "@/lib/tokenFilter";
import { filterTokens } from "@/lib/tokenFilter";
import { normalizeAddress } from "@/lib/utils/address";
import type { SimBalanceItem } from "./useSimTokenInfo";

/**
 * Build a stable cache key from a list of tokens.
 * Sorted so the same set of tokens in any order produces the same key.
 */
function buildTokenSetKey(tokens: TokenFilterInput[]): string {
  return tokens
    .map(t => `${t.chainId}:${normalizeAddress(t.address)}`)
    .sort()
    .join(",");
}

/**
 * Convert SimDune balance items to the format expected by the token filter service.
 */
function toFilterInputs(balances: SimBalanceItem[]): TokenFilterInput[] {
  return balances.map(b => ({
    chainId: b.chain_id,
    address: b.address,
    symbol: b.symbol,
    hasSimDunePrice: typeof b.price_usd === "number",
  }));
}

interface FilteredBalancesResult {
  /** Tokens that passed filtering */
  filtered: SimBalanceItem[];
  /** Tokens that were filtered out (spam/low-liquidity) */
  spam: SimBalanceItem[];
  /** Whether filtering has completed (false while loading or if service unavailable) */
  isFiltered: boolean;
  /** Whether the filter query is currently loading */
  isLoading: boolean;
}

/**
 * Hook that runs SimDune balance items through the token filter service.
 *
 * Returns the original balance items split into `filtered` (passed) and
 * `spam` (removed) arrays. While loading or if the service is unavailable,
 * all items are returned in `filtered` (fail-open).
 */
export function useFilteredTokens(
  balances: SimBalanceItem[] | undefined,
  options?: TokenFilterOptions & { enabled?: boolean },
): FilteredBalancesResult {
  const enabled = options?.enabled !== false;

  // Partition balances into "passthrough" vs "filter candidates":
  // - Solana: the token-filter worker doesn't support Solana chain IDs; the
  //   SVM balance endpoint already applies exclude_spam_tokens=true upstream.
  // - Manual chains (chain-registry `manualTokens`): the balance list is a
  //   curated allow-list, so there's nothing for a spam filter to do. The
  //   filter worker also doesn't recognize these chain IDs and would drop
  //   everything as "unknown chain" (e.g. pUSD on Plume 98866).
  const { passthroughBalances, filterCandidates } = useMemo(() => {
    if (!balances || balances.length === 0) return { passthroughBalances: [], filterCandidates: [] };
    const passthrough: SimBalanceItem[] = [];
    const candidates: SimBalanceItem[] = [];
    for (const b of balances) {
      const chainId = b.chain_id;
      if (chainId && (SOLANA_CHAIN_IDS.has(chainId) || hasManualTokens(chainId))) {
        passthrough.push(b);
      } else {
        candidates.push(b);
      }
    }
    return { passthroughBalances: passthrough, filterCandidates: candidates };
  }, [balances]);

  const filterInputs = useMemo(() => {
    if (filterCandidates.length === 0) return [];
    return toFilterInputs(filterCandidates);
  }, [filterCandidates]);

  const tokenSetKey = useMemo(() => buildTokenSetKey(filterInputs), [filterInputs]);

  const { data: filterResult, isLoading } = useQuery({
    queryKey: ["tokenFilter", tokenSetKey],
    queryFn: () => filterTokens(filterInputs, options),
    enabled: enabled && filterInputs.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    if (!balances || balances.length === 0) {
      return { filtered: [], spam: [], isFiltered: true, isLoading: false };
    }

    // Passthrough tokens (Solana + manual chains) always pass; they don't go
    // through the filter worker.
    if (!filterResult) {
      // Filter not done yet — passthrough tokens are considered filtered, the
      // rest is returned unfiltered (fail-open).
      return {
        filtered: passthroughBalances.length > 0 ? [...passthroughBalances, ...filterCandidates] : balances,
        spam: [],
        // isFiltered: true when all tokens are passthroughs — nothing was sent to
        // the filter worker, but there are no candidates left to filter either.
        isFiltered: passthroughBalances.length > 0 && filterCandidates.length === 0,
        isLoading,
      };
    }

    // Build a set of candidate tokens that passed for fast lookup
    const passedKeys = new Set(filterResult.tokens.map(t => `${t.chainId}:${normalizeAddress(t.address)}`));

    // Single-pass partition candidates into filtered vs spam
    const filteredCandidates: SimBalanceItem[] = [];
    const spam: SimBalanceItem[] = [];
    for (const b of filterCandidates) {
      const key = `${b.chain_id}:${normalizeAddress(b.address)}`;
      (passedKeys.has(key) ? filteredCandidates : spam).push(b);
    }

    // Merge: all passthrough tokens + filtered candidates
    return { filtered: [...passthroughBalances, ...filteredCandidates], spam, isFiltered: true, isLoading: false };
  }, [balances, passthroughBalances, filterCandidates, filterResult, isLoading]);
}
